import { TableDefinitionDto } from '@/components/table/types/TableDefinitionDto.ts';
import { filterFieldOptions } from '@/components/table/components/filter/filterFieldOptions.ts';
import { useTableStoreContext } from '@/components/table/contexts/TableContext.tsx';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import {
  FilterCriteriaForm,
  FiltersCriteriaInputForm,
} from '@/components/table/schemas/filterCriteriaSchema.ts';
import { DropdownMenuContent } from '@/components/ui/DropdownMenu.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { Plus } from 'lucide-react';
import React from 'react';
import { SingleFilterRow } from '@/components/table/components/filter/SingleFilterRow.tsx';
import { columnLabelClass } from '@/components/ui/typography.ts';
import { toolbarPopoverClass } from '@/components/table/components/toolbarSurface.ts';
import { cn } from '@/lib/utils.ts';
import {
  emptyFilter,
  filterFormDefaults,
} from '@/components/table/components/filter/filterFormDefaults.ts';

const FilterOptionsDropdown = ({
  tableDef,
  onClose,
}: {
  tableDef: TableDefinitionDto;
  onClose: () => void;
}) => {
  const filterCriteria = useTableStoreContext((state) => state.filterCriteria);
  const filterOptions = filterFieldOptions(
    tableDef,
    filterCriteria.filters.map((filter) => filter.fieldId),
  );
  const setFilters = useTableStoreContext((state) => state.setFilters);
  const setFiltersCombinator = useTableStoreContext(
    (state) => state.setFiltersCombinator,
  );

  const filterConfigForm = useForm<FiltersCriteriaInputForm>({
    defaultValues: filterFormDefaults(filterCriteria),
  });

  const { fields, remove, append } = useFieldArray({
    control: filterConfigForm.control,
    name: 'filters',
  });

  const clearFilters = () => {
    filterConfigForm.setValue('filters', [emptyFilter]);
    filterConfigForm.setValue('combinator', 'AND');
    setFilters([]);
  };

  const onFilterApply = (filterForm: FilterCriteriaForm) => {
    setFilters(filterForm.filters.filter((f) => f.fieldId));
    setFiltersCombinator(filterForm.combinator);
    onClose();
  };

  return (
    <DropdownMenuContent
      align="end"
      className={cn('w-[700px] space-y-2', toolbarPopoverClass)}
    >
      <div className="flex flex-row items-center justify-between pl-2">
        <div className={columnLabelClass}>Filtry</div>
        <ActionButton tone="ghost" size="toolbar" onClick={clearFilters}>
          Wyczyść filtry
        </ActionButton>
      </div>

      <FormProvider {...filterConfigForm}>
        <form
          className="space-y-1"
          onSubmit={filterConfigForm.handleSubmit(onFilterApply)}
        >
          {fields.map((field, index) => (
            <SingleFilterRow
              key={field.id}
              index={index}
              filterOptions={filterOptions}
              tableDef={tableDef}
              remove={remove}
              filtersNumber={fields.length}
            />
          ))}

          <div className="mt-2 flex flex-row items-center justify-between">
            <ActionButton
              tone="outline"
              size="toolbar"
              onClick={() => append(emptyFilter)}
              disabled={fields.length === 4}
            >
              <Plus />
              Dodaj filtr
            </ActionButton>
            <ActionButton tone="accent" size="toolbar" type="submit">
              Zastosuj filtry
            </ActionButton>
          </div>
        </form>
      </FormProvider>
    </DropdownMenuContent>
  );
};

export default FilterOptionsDropdown;
